import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL, CONTACT_EMAIL, CONTACT_MAILTO } from '../siteConfig';
import { useContent } from '../content/ContentContext';

const LESSON_TYPES = [
  { label: 'Interview lesson (new students)', duration: 30 },
  { label: '30-minute lesson', duration: 30 },
  { label: '45-minute lesson', duration: 45 },
  { label: '60-minute lesson', duration: 60 },
];

// Build a downloadable .ics event from a wall-clock local start time.
function downloadIcs(title: string, startLocal: string, durationMin: number, description: string) {
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
  const start = new Date(startLocal);
  const end = new Date(start.getTime() + durationMin * 60000);
  const uid = `${fmt(start)}-${Math.random().toString(36).slice(2)}@evenstarmusic.com`;
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//evenstarmusic//lesson//EN',
    'BEGIN:VEVENT', `UID:${uid}`, `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`,
    `SUMMARY:${title}`, `DESCRIPTION:${description}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'cello-lesson.ics';
  a.click();
  URL.revokeObjectURL(url);
}

function IcsDownload() {
  const [done, setDone] = useState(false);
  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const date = d.get('date');
    const time = d.get('time');
    if (!date || !time) return;
    const type = LESSON_TYPES.find(t => t.label === d.get('lessonType')) ?? LESSON_TYPES[0];
    downloadIcs('Cello lesson with Berti', `${date}T${time}`, type.duration, 'Cello lesson — evenstarmusic.com');
    setDone(true);
  }
  return (
    <details className="ics-block">
      <summary>Prefer to add it to your own calendar?</summary>
      <form className="booking__form" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="ics-type">Lesson</label>
          <select id="ics-type" name="lessonType" defaultValue={LESSON_TYPES[0].label}>
            {LESSON_TYPES.map(t => <option key={t.label}>{t.label}</option>)}
          </select>
        </div>
        <div className="booking__row">
          <div className="field">
            <label htmlFor="ics-date">Date</label>
            <input id="ics-date" name="date" type="date" required />
          </div>
          <div className="field">
            <label htmlFor="ics-time">Time</label>
            <input id="ics-time" name="time" type="time" required />
          </div>
        </div>
        <button type="submit" className="btn" style={{ justifySelf: 'start' }}>Download .ics</button>
        {done && <p className="booking__thanks">Added — open the downloaded file to drop it on your calendar.</p>}
      </form>
    </details>
  );
}

export function Schedule() {
  const { content } = useContent();
  const calendlyUrl = content.settings.calendlyUrl || CALENDLY_URL;
  return (
    <main className="stub stub--doc">
      <Link to="/" className="stub__back">← Back to home</Link>
      <h2>Schedule a Lesson</h2>

      {calendlyUrl ? (
        <>
          <p>
            Pick a time that works for you and Berti will confirm. New students start
            with an interview lesson; times show in your own timezone.
          </p>
          <div className="calendly-embed">
            <iframe
              src={`${calendlyUrl}${calendlyUrl.includes('?') ? '&' : '?'}hide_gdpr_banner=1`}
              title="Book a lesson with Berti"
              loading="lazy"
            />
          </div>
        </>
      ) : (
        <p>
          Online booking is coming soon. For now, please email{' '}
          {CONTACT_EMAIL ? <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a> : 'us'} with your
          name, the student's age and experience, and your preferred days/times, and
          Berti will reply to set up an interview lesson.
        </p>
      )}

      <IcsDownload />

      <p style={{ marginTop: 40 }}>
        <Link to="/lesson-policies" className="btn">Read lesson policies</Link>
      </p>
    </main>
  );
}
